import {create} from "zustand";
import {persist} from "zustand/middleware";

export interface CartItem {
    id: string;
    product_item_id: string | number;
    title: string;
    price: number;
    quantity: number;
    seller_title: string;
    slug: string;
    discount_percentage: string | number;
    sale_price: string | number;
    image: string | null;
    unique_id: string | null;
    active: boolean | number;
    max_basket_limit: number;
    min_total_qty_and_max_basket_qty: number;
}

interface CartStore {
    card_status: "current" | "next";
    items: CartItem[];
    addItem: (item: CartItem) => void;
    removeItem: (id: string | null | undefined) => void;
    updateQuantity: (id: string | null, quantity: number) => void;
    updateStatus: (id: string | null, active: boolean) => void;
    changeCardStatus: (status: "current" | "next") => void;
    clearCart: () => void;
    clearActiveItems: (items: any[]) => void;
    updateEntireCart: (newItems: CartItem[]) => void;
    replaceEntireCart: (newItems: CartItem[]) => void;
}

export const useCartStore = create<CartStore>()(
    persist(
        (set) => ({
            card_status: "current",
            items: [],
            addItem: (item) => set((state) => {
                const existingItem = state?.items?.find((i) => i.unique_id === item.unique_id);
                if (existingItem) {
                    return {
                        items: state.items.map((i) => i.unique_id === item.unique_id ? {
                            ...i,
                            quantity: i.quantity + 1
                        } : i),
                    };
                }
                return {items: [...state.items, item]};
            }),
            removeItem: (unique_id) => set((state) => ({
                items: state.items.filter((i) => i.unique_id !== unique_id),
            })),
            updateQuantity: (unique_id, quantity) => set((state) => ({
                items: state.items.map((i) => i.unique_id === unique_id ? {...i, quantity} : i),
            })),
            updateStatus: (unique_id, active) => set((state) => ({
                items: state.items.map((i) => i.unique_id === unique_id ? {...i, active} : i),
            })),
            changeCardStatus: (status: "current" | "next") => set(() => ({
                card_status: status,
            })),
            clearCart: () => set({items: []}),
            clearActiveItems: (items: CartItem[]) => set({ items: items?.filter((item: CartItem) => item?.active === 1) }),
            replaceEntireCart: (newItems) => set({
                items: newItems?.map((i: any) => (
                    {...i}
                ))
            }),
            updateEntireCart: (newItems) => set((state) => {
                const mergedMap = new Map<string, CartItem>();
                const existingItemsMap = new Map(state.items.map(item => [item.unique_id, item]));

                newItems.forEach((item) => {
                    if (item.unique_id) {
                        const existingItem = existingItemsMap.get(item.unique_id);
                        mergedMap.set(item.unique_id, {
                            ...existingItem,
                            ...item,
                            quantity: Math.max(existingItem?.quantity || 0, item.quantity),
                            active: item.active,
                        });
                    }
                });

                state.items.forEach((item) => {
                    if (item.unique_id && !mergedMap.has(item.unique_id)) {
                        mergedMap.set(item.unique_id, item);
                    }
                });

                const mergedItems = Array.from(mergedMap.values());

                // Check if the mergedItems array is different from the current items array
                if (JSON.stringify(mergedItems) !== JSON.stringify(state.items)) {
                    return {items: mergedItems};
                }
                return state; // Return the current state if no changes
            }),
            // updateEntireCart: (newItems) => set((state) => {
            //     const mergedMap = new Map<string, CartItem>();
            //     newItems.forEach((item) => {
            //         if (item.unique_id) {
            //             mergedMap.set(item.unique_id, item); // فقط از newItems استفاده کن
            //         }
            //     });
            //     const mergedItems = Array.from(mergedMap.values());
            //     return { items: mergedItems };
            // }),
        }),
        {
            name: "cart-storage",
        }
    )
);